import { graphql } from "gatsby"
import React from "react"
import styled from "styled-components"
import Breakpoints from "../../../components/shared/breakpoints"
import { BackgroundColourContextProvider } from "../../util/background-colour-context"
import VariantContainer, {
  IStyleVariant,
  IVariantContainerProps,
} from "../variant-container"

interface ISpacerStyleElementProps extends IVariantContainerProps {
  $display?: IStyleVariant
  $row?: IStyleVariant
  $height?: IStyleVariant
}
interface ISpacerProps {
  display?: IStyleVariant
  row?: IStyleVariant
  height?: IStyleVariant
  className?: string
  variants?: IStyleVariant
}

const SpacerElement = styled(VariantContainer)<ISpacerStyleElementProps>`
  display: block;
  grid-column: 1 / -1;
  width: 100%;

  display: ${(props) => props?.$display?.sm};
  grid-row: ${(props) => props?.$row?.sm};
  height: ${(props) => props?.$height?.sm};

  @media (min-width: ${Breakpoints.md}) {
    display: ${(props) => props?.$display?.md};
    grid-row: ${(props) => props?.$row?.md};
    height: ${(props) => props?.$height?.md};
  }
  @media (min-width: ${Breakpoints.lg}) {
    display: ${(props) => props?.$display?.lg};
    grid-row: ${(props) => props?.$row?.lg};
    height: ${(props) => props?.$height?.lg};
  }
`

export const Spacer = ({
  display,
  height,
  row,
  className,
  variants,
  ...rest
}: ISpacerProps) => {
  return (
    <SpacerElement
      $display={display}
      $height={height}
      $row={row}
      className={className}
      variants={variants}
      {...rest}
    />
  )
}

interface IHeightOptions {
  "X-Small": string
  Small: string
  Medium: string
  Large: string
  "X-Large": string
}

const mobileHeights: IHeightOptions = {
  "X-Small": "16px",
  Small: "24px",
  Medium: "32px",
  Large: "48px",
  "X-Large": "64px",
}

const tabletHeights: IHeightOptions = {
  "X-Small": "24px",
  Small: "32px",
  Medium: "48px",
  Large: "64px",
  "X-Large": "96px",
}

const desktopHeights: IHeightOptions = {
  "X-Small": "32px",
  Small: "48px",
  Medium: "64px",
  Large: "96px",
  "X-Large": "128px",
}

type THeightOptions = keyof IHeightOptions

export interface IContentfulSpacer {
  __typename: "ContentfulSpacer"
  mobile: THeightOptions
  tablet?: THeightOptions
  desktop?: THeightOptions
  backgroundColour?: { hex: string }
}

/**
 * Adapter function for Contentful Spacer to Breakpoint Spacer
 */
export const ContentfulSpacer = ({
  mobile,
  tablet,
  desktop,
  backgroundColour,
}: Omit<IContentfulSpacer, "__typename">) => (
  <BackgroundColourContextProvider colour={backgroundColour?.hex}>
    {/* cascade the spacer values up breakpoints */}
    {mobile ? (
      <Spacer
        height={{
          sm: mobileHeights[mobile],
          md: tabletHeights[tablet ? tablet : mobile],
          lg: desktopHeights[desktop ? desktop : tablet ? tablet : mobile],
        }}
      />
    ) : null}
  </BackgroundColourContextProvider>
)

export const ContentfulSpacerFragment = graphql`
  fragment ContentfulSpacer on ContentfulSpacer {
    contentful_id
    mobile
    tablet
    desktop
    backgroundColour {
      hex
    }
  }
`
export const CONTENTFUL_SPACER_TYPENAME = "ContentfulSpacer"
export default Spacer
